import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { useEffect, useRef, useState } from "react";
import * as api from "./api";
import { AreaShapeEditor } from "./AreaEditor";
import { AreaShapeImport } from "./AreaImport";
import { AreaToggle, useAreaShapes } from "./AreaLayer";
import { outsideText, pinAreaText, readAreas, ringOf, writeAreas } from "./areaMap";
import { go, useSearchState } from "./hooks";
import { accountName, sideWords } from "./fill";
import { sourceTagText } from "./dataSource";
import { legendOf, pinColour, pinCountText, pinListParts, readLayer, readMapMode, statusLabel, writeLayer, writeMapMode } from "./mapView";
import { type GoogleMap, loadMaps, type Maps, type Marker, type Polyline, watchMapErrors } from "./maps";
import { CHECK_PIN, PIN_AT_DOOR, usePinLayer } from "./PinLayer";
import { ClusterKey, FillBandKeys, LevelDot } from "./LevelDot";
import { type MapInfo, MapInfoBox } from "./MapInfoBox";
import { MapFullscreenButton } from "./MapFullscreen";
import { ConeDot, useMarkerCone } from "./ConeCard";
import { download } from "./rebates";
import { useMedia } from "./Menu";
import { addStops, googleDirUrls, legWaypoints, MAX_STOPS, moveStop, nearestNeighbourOrder, routeCsv, routePoints, type Stop } from "./salesRoute";
import { SERVER_PROBLEM } from "./save";
import { Skeleton } from "./Skeleton";

const NO_POINT = { lat: 39.8, lng: -98.6 };
const LIST_SHOWN = 100;
const ROUTE_COLOUR = "#ffc98a";
const DARK = "#0f1216";
const NARROW = "(max-width: 40rem)";

type Start = "first" | "gps";

const asStop = (pin: api.Pin): Stop => ({ id: pin.id, name: pin.name, address: pin.address, lat: pin.lat, lng: pin.lng });

/** Spec F-MAP-1 to F-MAP-3 and F-SAL-9: the pins of a list on a Google map, and a sales route. */
export function PinMap({
  kind,
  filters,
  base,
  auth,
  user,
  reload = 0,
  timeZone,
}: {
  kind: api.PinKind;
  filters: Record<string, string>;
  base: string;
  auth: Auth;
  user: User;
  /** A new value fetches the pins again. */
  reload?: number;
  timeZone?: string;
}) {
  const element = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const map = useRef<GoogleMap | null>(null);
  const markers = useRef<Marker[]>([]);
  const line = useRef<Polyline | null>(null);
  const onPin = useRef<(pin: api.Pin, marker: Marker) => void>(() => undefined);
  const opener = useRef<Marker | null>(null);
  const onStop = useRef<(id: string) => void>(() => undefined);
  const [maps, setMaps] = useState<Maps | null>(null);
  const [mapState, setMapState] = useState<"loading" | "ready" | "failed">("loading");
  const [layer, setLayerState] = useState<api.PinLayer>(() => (kind === "accounts" ? readLayer(location.search) : "door"));
  const [areasOn, setAreasOn] = useState(() => readAreas(location.search));
  const [shaping, setShaping] = useState(false);
  const [shapeId, setShapeId] = useState<string | null>(null);
  const [shapeNote, setShapeNote] = useState("");
  const [importing, setImporting] = useState(false);
  const [canImport, setCanImport] = useState(true);
  const [historyHost, setHistoryHost] = useState<HTMLElement | null>(null);
  const narrow = useMedia(NARROW);
  const [picked, setPicked] = useState<api.Pin | null>(null);
  const cone = useMarkerCone();
  const [planning, setPlanning] = useState(false);
  const [stops, setStops] = useState<Stop[]>([]);
  const [start, setStart] = useState<Start>("first");
  const [gps, setGps] = useState<api.LatLng | null>(null);
  const [locating, setLocating] = useState(false);
  const [note, setNote] = useState("");
  const origin = start === "gps" ? gps : null;
  const points = routePoints(stops, origin).map(({ lat, lng }) => ({ lat, lng }));
  const waypoints = legWaypoints(narrow);
  const urls = googleDirUrls(points, waypoints);
  const onRoute = new Set(stops.map((stop) => stop.id));

  // Each new map counts against the daily Maps quota, so the map loads only in the Map view.
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(loadMaps)
      .then((loaded) => {
        if (!live) return;
        setMaps(loaded);
        setMapState("ready");
      })
      .catch(() => live && setMapState("failed"));
    return () => {
      live = false;
    };
  }, [user]);

  useEffect(() => {
    const target = element.current;
    if (mapState !== "ready" || !target) return;
    return watchMapErrors(target, () => setMapState("failed"));
  }, [mapState]);

  useEffect(() => {
    if (!maps || !element.current || map.current) return;
    const made = new maps.maps.Map(element.current, {
      center: NO_POINT,
      zoom: 4,
      mapId: maps.mapId,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      clickableIcons: false,
    });
    map.current = made;
  }, [maps]);
  const { found, list, loading, failed, retry } = usePinLayer(maps, map, user, {
    kind,
    layer,
    filters,
    reload,
    skip: stops.map((stop) => stop.id),
    onPin: (pin, marker) => onPin.current(pin, marker),
    watch: cone.watch,
  });
  const openShape = (area: api.Area) => {
    const view = map.current;
    const ring = ringOf(area.polygon);
    setShapeId(area.id);
    setShapeNote("");
    if (!maps || !view || !ring.length) return;
    const bounds = new maps.maps.LatLngBounds();
    for (const point of ring) bounds.extend(point);
    view.fitBounds(bounds, 48);
  };
  const areaShapes = useAreaShapes(kind === "accounts" ? maps : null, map, user, areasOn || shaping, {
    hide: shapeId,
    onPick:
      shaping && !shapeId
        ? (area) => {
            if (area.can_edit_shape) openShape(area);
          }
        : undefined,
  });
  const areaCount = areaShapes.count;
  const shapeAreas = areaShapes.areas.filter((area) => area.can_edit_shape);
  const shapeArea = areaShapes.areas.find((area) => area.id === shapeId) ?? null;

  const routeKey = JSON.stringify(points);
  useEffect(() => {
    const view = map.current;
    if (!maps || !view) return;
    for (const marker of markers.current) marker.map = null;
    const made: Marker[] = [];
    const add = (options: Record<string, unknown>, click: () => void) => {
      const marker = new maps.marker.AdvancedMarkerElement({ map: view, gmpClickable: true, ...options });
      marker.addListener("click", click);
      made.push(marker);
    };
    points.forEach((point, index) => {
      const first = origin !== null && index === 0;
      const number = origin ? index : index + 1;
      const glyph = new maps.marker.PinElement({ glyph: first ? "S" : String(number), background: ROUTE_COLOUR, borderColor: DARK, glyphColor: DARK });
      const stop = first ? null : stops[number - 1];
      add({ position: point, title: stop ? `Stop ${number}: ${stop.name}` : "Your position", content: glyph.element, zIndex: 1000 + index }, () => {
        if (stop) onStop.current(stop.id);
      });
    });
    markers.current = made;
    line.current ??= new maps.maps.Polyline({ strokeColor: ROUTE_COLOUR, strokeOpacity: 0.9, strokeWeight: 3, clickable: false });
    line.current.setPath(points);
    line.current.setMap(points.length > 1 ? view : null);
  }, [maps, routeKey]);

  useEffect(
    () => () => {
      for (const marker of markers.current) marker.map = null;
      line.current?.setMap(null);
    },
    [],
  );

  const ordered = (next: Stop[], from: api.LatLng | null = origin) => nearestNeighbourOrder(next, from);
  const addPins = (pins: api.Pin[]) => {
    const added = addStops(stops, pins.map(asStop));
    setStops(ordered(added.stops));
    setNote(added.left ? `A route holds ${MAX_STOPS} stops. ${added.left} pins were not added.` : "");
  };
  const toggle = (pin: api.Pin) => {
    if (onRoute.has(pin.id)) setStops(stops.filter((stop) => stop.id !== pin.id));
    else addPins([pin]);
  };
  onPin.current = (pin, marker) => {
    opener.current = marker;
    setPicked(pin);
    if (planning) toggle(pin);
  };
  onStop.current = (id) => {
    if (planning) setStops(stops.filter((stop) => stop.id !== id));
  };
  const locate = () => {
    if (!("geolocation" in navigator)) {
      setNote("This browser cannot give a GPS position. The route starts at the first stop.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        const here = { lat: fix.coords.latitude, lng: fix.coords.longitude };
        setLocating(false);
        setGps(here);
        setStart("gps");
        setStops((current) => ordered(current, here));
        setNote("");
      },
      (error) => {
        setLocating(false);
        setStart("first");
        const denied = error.code === error.PERMISSION_DENIED;
        setNote(`${denied ? "Allow the location for this site to start from your position." : "No GPS position."} The route starts at the first stop.`);
      },
      { enableHighAccuracy: false, timeout: 20_000, maximumAge: 60_000 },
    );
  };
  const pickStart = (next: Start) => {
    if (next === "gps") {
      locate();
      return;
    }
    setStart("first");
    setStops(ordered(stops, null));
  };
  const showAreas = (next: boolean) => {
    setAreasOn(next);
    history.replaceState(history.state, "", `${location.pathname}${writeAreas(location.search, next)}${location.hash}`);
  };
  const setLayer = (next: api.PinLayer) => {
    setLayerState(next);
    history.replaceState(history.state, "", `${location.pathname}${writeLayer(location.search, next)}${location.hash}`);
  };
  const link = (pin: api.Pin) => `${base}/${kind}/${pin.id}`;
  const noun = kind === "accounts" ? "account" : "lead";
  const accounts = kind === "accounts";
  const info = (pin: api.Pin): MapInfo => ({
    id: pin.id,
    accountId: accounts ? pin.id : null,
    title: statusLabel(kind, pin.status),
    name: accounts ? accountName(pin.number, pin.name) : pin.name,
    share: pin.fill,
    fill: accounts,
    fillText: pin.float ? sideWords(pin.float, pin.fill) : undefined,
    lines: [
      pin.fallback && PIN_AT_DOOR,
      pin.check && CHECK_PIN,
      pin.address,
      pin.data_source && `Source: ${sourceTagText(pin.data_source)}`,
      accounts && pinAreaText(pin),
      pin.outside_area && outsideText(pin.outside_area),
    ],
    href: link(pin),
    linkText: `Open the ${noun}`,
  });

  if (found === "hidden") {
    return (
      <p data-testid="pin-map-hidden">Your role does not show {kind} on the map.</p>
    );
  }
  const shown = list?.pins ?? [];
  return (
    <section className="pin-map" data-testid="pin-map" data-kind={kind} aria-label={`Map of the ${kind}`}>
      <div className="pin-map-bar">
        {kind === "accounts" && (
          <label>
            Pins at{" "}
            <select data-testid="pin-layer" value={layer} onChange={(e) => setLayer(e.target.value as api.PinLayer)}>
              <option value="door">Front door</option>
              <option value="tank">Container pin</option>
            </select>
          </label>
        )}
        {areaCount > 0 && <AreaToggle on={areasOn} onChange={showAreas} />}
        {shapeAreas.length > 0 && maps && (
          <button
            type="button"
            aria-pressed={shaping}
            data-testid="area-shape"
            disabled={shapeId !== null || importing}
            onClick={() => {
              setShaping(!shaping);
              setShapeNote("");
            }}
          >
            {shaping ? "Stop editing shapes" : "Edit shape"}
          </button>
        )}
        {shapeAreas.length > 0 && maps && canImport && (
          <button
            type="button"
            aria-expanded={importing}
            data-testid="area-import-open"
            disabled={shaping}
            onClick={() => {
              setImporting(!importing);
              setShapeNote("");
            }}
          >
            Import shapes
          </button>
        )}
        <button type="button" aria-pressed={planning} data-testid="route-plan" onClick={() => setPlanning(!planning)}>
          {planning ? "Stop planning" : "Plan a route"}
        </button>
      </div>
      {importing && maps && (
        <AreaShapeImport
          maps={maps}
          map={map}
          user={user}
          base={base}
          onApplied={() => {
            areaShapes.reload();
            showAreas(true);
          }}
          onUnavailable={() => {
            setImporting(false);
            setCanImport(false);
            setShapeNote("This server cannot import shapes yet. Try again after the next release.");
          }}
          onClose={() => setImporting(false)}
        />
      )}
      {shaping && !shapeArea && (
        <div className="pin-map-bar" data-testid="area-shape-pick">
          <label>
            Area{" "}
            <select
              data-testid="area-shape-area"
              value=""
              onChange={(e) => {
                const area = shapeAreas.find((other) => other.id === e.target.value);
                if (area) openShape(area);
              }}
            >
              <option value="">Choose…</option>
              {shapeAreas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.code}
                  {area.polygon_source === "user" ? " · changed by a person" : ""}
                </option>
              ))}
            </select>
          </label>
          <span className="sub">or tap an area on the map.</span>
        </div>
      )}
      {shaping && shapeArea && maps && (
        <AreaShapeEditor
          key={shapeArea.id}
          maps={maps}
          map={map}
          user={user}
          area={shapeArea}
          onSaved={(area, text) => {
            areaShapes.replace(area);
            setShapeId(null);
            setShapeNote(text);
          }}
          onClose={() => setShapeId(null)}
          historyHost={historyHost}
          timeZone={timeZone}
          mapBox={wrap}
        />
      )}
      {shapeNote && (
        <p className="sub" role="status" data-testid="area-shape-note">
          {shapeNote}
        </p>
      )}
      <p className="sub" role="status" data-testid="pin-count" data-count={list ? shown.length : undefined}>
        {list ? pinCountText(kind, shown.length, list.total, list.truncated) : loading ? "Loading the pins…" : ""}
        {list && list.unplaced > 0 && ` · ${list.unplaced.toLocaleString("en-US")} with no point on the map`}
        {list && layer === "tank" && " · Where the container pin is not set, the dot is at the front door."}
        {list && loading && " · Updating…"}
      </p>
      {(found === "session" || failed === "session") && (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" className="primary" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      )}
      {(found === "server" || found === "network" || failed === "server" || failed === "network") && (
        <p role="alert" className="error" data-testid="pin-map-failed">
          {found === "server" || failed === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}
          {failed && " The map shows the pins of the last check."}{" "}
          <button type="button" onClick={retry}>
            Try again
          </button>
        </p>
      )}
      <ul className="pin-legend" aria-label={accounts ? "Dot colours" : "Pin colours"}>
        {accounts ? (
          <>
            <FillBandKeys />
            <li>
              <LevelDot share={null} small /> No estimate
            </li>
            <li>
              <LevelDot share={0.15} check small /> Check the pin
            </li>
            <ClusterKey />
          </>
        ) : (
          legendOf(kind).map((item) => (
            <li key={item.status}>
              <span className="pin-swatch" style={{ background: item.colour }} aria-hidden="true" />
              {item.label}
            </li>
          ))
        )}
        {planning && (
          <li>
            <span className="pin-swatch" style={{ background: ROUTE_COLOUR }} aria-hidden="true" />
            Route stop
          </li>
        )}
      </ul>
      <div className={shaping && shapeArea ? "pin-map-side shaping" : "pin-map-side"}>
        <div ref={wrap} className="pin-map-wrap">
          <div ref={element} className="pin-map-canvas" data-testid="pin-map-canvas" data-state={mapState} role="region" aria-label={`Map of the ${kind}`} />
          {mapState === "ready" && <MapFullscreenButton wrap={wrap} testId="pin-map-fullscreen" />}
          {mapState === "loading" && <Skeleton shape="map" overlay label="Loading the map" />}
          {mapState === "failed" && (
            <p className="location-map-note map-warning" data-testid="pin-map-no-google">
              The map cannot load. The list below shows the {kind}.
            </p>
          )}
          {cone.card}
          {picked && (
            <MapInfoBox
              testId="pin-card"
              info={info(picked)}
              onClose={() => {
                setPicked(null);
                (opener.current as unknown as HTMLElement | null)?.focus?.();
              }}
            />
          )}
        </div>
        {shaping && shapeArea && <div ref={setHistoryHost} className="pin-map-aside" data-testid="area-shape-aside" />}
      </div>
      {planning && <p className="sub">Tap the pins to add stops, or tap a stop again to remove it.</p>}
      {planning && (
        <section className="route-panel" data-testid="route-panel" aria-labelledby="route-title">
          <h2 id="route-title">Sales route</h2>
          <div className="actions">
            <button type="button" data-testid="route-add-all" disabled={!shown.length} onClick={() => addPins(shown)}>
              Add all in view ({Math.min(shown.length, MAX_STOPS)})
            </button>
            {stops.length > 1 && (
              <button type="button" data-testid="route-order" onClick={() => setStops(ordered(stops))}>
                Order nearest first
              </button>
            )}
            {stops.length > 0 && (
              <button type="button" className="quiet" data-testid="route-clear" onClick={() => setStops([])}>
                Clear the route
              </button>
            )}
          </div>
          <fieldset className="route-start">
            <legend>Start</legend>
            <label>
              <input type="radio" name="route-start" data-testid="route-start-first" checked={start === "first"} onChange={() => pickStart("first")} /> The first stop
            </label>
            <label>
              <input type="radio" name="route-start" data-testid="route-start-gps" checked={start === "gps"} disabled={locating} onChange={() => pickStart("gps")} />{" "}
              {locating ? "Finding your position…" : "My location"}
            </label>
          </fieldset>
          {note && (
            <p role="status" className="sub" data-testid="route-note">
              {note}
            </p>
          )}
          {stops.length === 0 ? (
            <p data-testid="route-empty">No stops yet.</p>
          ) : (
            <ol className="route-stops" data-testid="route-stops" data-count={stops.length}>
              {stops.map((stop, index) => (
                <li key={stop.id} data-testid="route-stop" data-id={stop.id}>
                  <span className="route-stop-number" style={{ background: ROUTE_COLOUR }}>
                    {index + 1}
                  </span>
                  <span className="route-stop-name">
                    {stop.name}
                    {stop.address && <span className="sub"> · {stop.address}</span>}
                  </span>
                  <span className="route-stop-tools">
                    <button type="button" aria-label={`Move ${stop.name} up`} disabled={index === 0} onClick={() => setStops(moveStop(stops, index, -1))}>
                      ↑
                    </button>
                    <button type="button" aria-label={`Move ${stop.name} down`} disabled={index === stops.length - 1} onClick={() => setStops(moveStop(stops, index, 1))}>
                      ↓
                    </button>
                    <button type="button" aria-label={`Remove ${stop.name}`} data-testid="route-remove" onClick={() => setStops(stops.filter((other) => other.id !== stop.id))}>
                      ✕
                    </button>
                  </span>
                </li>
              ))}
            </ol>
          )}
          {urls.length > 0 && (
            <div className="actions route-links">
              {urls.map((url, index) => (
                <a key={url} className="button" href={url} target="_blank" rel="noopener noreferrer" data-testid="route-open">
                  {urls.length === 1 ? "Open in Google Maps" : `Open leg ${index + 1} of ${urls.length} in Google Maps`}
                </a>
              ))}
              <button type="button" data-testid="route-csv" onClick={() => download(`route-${new Date().toISOString().slice(0, 10)}.csv`, routeCsv(stops))}>
                Download CSV
              </button>
            </div>
          )}
          {urls.length > 0 && (
            <p className="sub" data-testid="route-hint">
              Opens in Google Maps. On a phone, the Maps app shows every stop.
            </p>
          )}
          {urls.length > 1 && (
            <p className="sub">Google Maps takes {waypoints} stops between the start and the end of each link, so the route has legs.</p>
          )}
          <p className="sub">The app does not save the route.</p>
        </section>
      )}
      {list && shown.length > 0 && (
        <details className="pin-list" data-testid="pin-list" open={mapState === "failed" || undefined}>
          <summary>
            List of the {noun}s in this view{shown.length > LIST_SHOWN ? ` (the first ${LIST_SHOWN})` : ""}
          </summary>
          <ul>
            {shown.slice(0, LIST_SHOWN).map((pin) => (
              <li key={pin.id} data-testid="pin-row" data-id={pin.id}>
                {accounts ? <ConeDot share={pin.fill ?? null} small accountId={pin.id} label={accountName(pin.number, pin.name)} words={pin.float ? sideWords(pin.float, pin.fill) : undefined} /> : <span className="pin-swatch" style={{ background: pinColour(kind, pin.status) }} aria-hidden="true" />}
                <a href={link(pin)} onClick={go}>
                  {pinListParts(kind, pin).name}
                </a>
                <span className="sub">
                  {" "}
                  · {pinListParts(kind, pin).rest.join(" · ")}
                  {pin.check && " · check the pin"}
                </span>
                {planning && (
                  <button type="button" data-testid="pin-row-route" aria-pressed={onRoute.has(pin.id)} onClick={() => toggle(pin)}>
                    {onRoute.has(pin.id) ? "Remove from the route" : "Add to the route"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/** The List or Map switch of a list page; the page keeps the choice in the URL. */
export function ViewSwitch({ map, onPick }: { map: boolean; onPick: (map: boolean) => void }) {
  return (
    <div className="view-switch" role="group" aria-label="Show as">
      <button type="button" aria-pressed={!map} data-testid="view-list" onClick={() => onPick(false)}>
        List
      </button>
      <button type="button" aria-pressed={map} data-testid="view-map" onClick={() => onPick(true)}>
        Map
      </button>
    </div>
  );
}

/** The List or Map choice of the page, kept in the URL so a reload keeps it. */
export function useMapMode() {
  const [map, setMap] = useSearchState(readMapMode);
  const pick = (next: boolean) => {
    setMap(next);
    history.replaceState(history.state, "", `${location.pathname}${writeMapMode(location.search, next)}${location.hash}`);
  };
  return [map, pick] as const;
}
